import { useCallback, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardWrapper from 'components/wrappers/DashboardWrapper';
import ToastAlert from 'components/ToastAlert';
import TableContainer from 'components/TableContainer';
import TeamUserFormModal from './TeamUserFormModal';
import TeamUserService from 'api/services/TeamUserService';
import AuthService from 'api/services/AuthService';
import PropTypes from 'prop-types';

function TeamUserNameCell({ value }) {
    return <span className="font-medium text-gray-800 text-sm">{value}</span>;
}

function TeamUserEmailCell({ value }) {
    return <span className="text-gray-600 text-sm">{value}</span>;
}

function TeamUserRoleCell({ value }) {
    return value
        ? <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">{value}</span>
        : null;
}

function TeamUserCreatedAtCell({ value }) {
    return (
        <span className="text-gray-500 text-sm">
            {value ? new Date(value).toLocaleDateString() : '—'}
        </span>
    );
}

function TeamUserActionsCell({ row, column }) {
    const { t } = useTranslation();
    const user = row.original;
    const isSelf = user.id === column.currentUserId;
    return (
        <div className="flex justify-end gap-2">
            <button
                onClick={() => column.setEditTarget(user)}
                className="text-xs px-3 py-1 border rounded text-gray-600 hover:bg-gray-50"
            >
                {t('actions.edit')}
            </button>
            <button
                onClick={() => !isSelf && column.setDeleteTarget({ id: user.id, name: user.name })}
                disabled={isSelf}
                title={isSelf ? t('team_users.cannot_delete_self') : ''}
                className="text-xs px-3 py-1 border rounded text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
            >
                {t('actions.delete')}
            </button>
        </div>
    );
}

function TeamUsers() {
    const { t } = useTranslation();
    const currentUserId = AuthService.getCurrentUser()?.id;

    const [users, setUsers] = useState(null); // null = loading, [] = empty
    const [toast, setToast] = useState(null);

    const showSuccess = () => setToast({ type: 'success', message: t('alert.success_operation'), key: Date.now() });
    const showError = (err) => setToast({ type: 'error', message: err?.response?.data?.message || t('alert.failed_operation'), key: Date.now() });

    const [createOpen, setCreateOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const fetchUsers = useCallback(() => {
        TeamUserService.getTeamUsers({ limit: 100 })
            .then(data => setUsers(data.results ?? []))
            .catch((err) => setToast({ type: 'error', message: err?.response?.data?.message || t('alert.failed_operation'), key: Date.now() }));
    }, [t]);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    const handleCreate = async (data) => {
        try {
            await TeamUserService.createTeamUser(data);
            showSuccess();
            fetchUsers();
        } catch (err) {
            showError(err);
        }
    };

    const handleUpdate = async (data) => {
        try {
            await TeamUserService.updateTeamUser(editTarget.id, data);
            showSuccess();
            fetchUsers();
        } catch (err) {
            showError(err);
        }
    };

    const handleDelete = async () => {
        try {
            await TeamUserService.deleteTeamUser(deleteTarget.id);
            showSuccess();
            setDeleteTarget(null);
            fetchUsers();
        } catch (err) {
            showError(err);
            setDeleteTarget(null);
        }
    };

    const columns = useMemo(() => [
        {
            Header: t('team_users.name'),
            accessor: 'name',
            Cell: TeamUserNameCell,
        },
        {
            Header: t('team_users.email'),
            accessor: 'email',
            Cell: TeamUserEmailCell,
        },
        {
            Header: t('team_users.role'),
            accessor: 'role.name',
            Cell: TeamUserRoleCell,
        },
        {
            Header: t('team_users.created_at'),
            accessor: 'createdAt',
            Cell: TeamUserCreatedAtCell,
        },
        {
            Header: '',
            accessor: 'id',
            disableSortBy: true,
            Cell: TeamUserActionsCell,
            setEditTarget,
            setDeleteTarget,
            currentUserId,
        },
    ], [t, currentUserId, setEditTarget, setDeleteTarget]);

    return (
        <DashboardWrapper>
            <div className="p-6">

                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800">{t('team_users.title')}</h2>
                        <p className="text-sm text-gray-500 mt-1">{t('team_users.description')}</p>
                    </div>
                    <button
                        onClick={() => setCreateOpen(true)}
                        className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                    >
                        + {t('team_users.add_user')}
                    </button>
                </div>

                {/* Table with built-in search + pagination */}
                <TableContainer
                    data={Array.isArray(users) ? users : []}
                    columns={columns}
                    globalSearch={true}
                    isColumnFiltrable={false}
                />

                {/* Delete confirmation */}
                {deleteTarget && (
                    <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
                        <div className="bg-white rounded shadow-lg p-6 max-w-sm w-full">
                            <p className="text-gray-800 font-medium mb-1">{t('team_users.delete_confirm')}</p>
                            <p className="text-gray-500 text-sm mb-4">{deleteTarget.name}</p>
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => setDeleteTarget(null)}
                                    className="px-4 py-2 text-sm border rounded text-gray-600 hover:bg-gray-50"
                                >
                                    {t('actions.cancel')}
                                </button>
                                <button
                                    onClick={handleDelete}
                                    className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                                >
                                    {t('actions.delete')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <TeamUserFormModal
                    open={createOpen}
                    onClose={() => setCreateOpen(false)}
                    onSubmit={handleCreate}
                    user={null}
                />
                <TeamUserFormModal
                    open={!!editTarget}
                    onClose={() => setEditTarget(null)}
                    onSubmit={handleUpdate}
                    user={editTarget}
                />

                {toast && <ToastAlert key={toast.key} message={toast.message} type={toast.type} timeOut={4000} onClose={() => setToast(null)} />}
            </div>
        </DashboardWrapper>
    );
}

export default TeamUsers;

TeamUserNameCell.propTypes = {
  value: PropTypes.string,
};

TeamUserEmailCell.propTypes = {
  value: PropTypes.string,
};

TeamUserRoleCell.propTypes = {
  value: PropTypes.string,
};

TeamUserCreatedAtCell.propTypes = {
  value: PropTypes.string,
};

TeamUserActionsCell.propTypes = {
  row: PropTypes.object.isRequired,
  column: PropTypes.object.isRequired,
};
